import React, { FC, HTMLAttributes, memo } from 'react'
import { mergeClassNames } from '../../../../utils/commonFunctions';
import './Table.scss'

interface ComponentProps extends HTMLAttributes<HTMLDivElement> {
    children?: React.ReactNode;
    className?: string;
}

type CellTypes = 'extra__small' | 'small' | 'extra__medium' | 'medium' | 'large' | 'extra__large' | 'flexible'

/**
 * A container component for a table.
 *
 * This component is responsible for rendering a container for a table.
 * It expects its children to be Table components.
 *
 * @param {React.ReactNode} children - The content of the container.
 * @param {string} className - Additional CSS classes to apply to the container.
 * @param {object} props - Additional props to pass to the container element.
 */
export const TableContainer: FC<ComponentProps> = memo(({ children, className = '', ...props }) => {
    return (
        <div className={mergeClassNames(`custom__table__container`, className)} {...props}>
            {children}
        </div>
    )
})
TableContainer.displayName = 'TableContainer'

/**
 * A component that represents a table.
 *
 * This component is responsible for rendering a table.
 * It expects its children to be TableHeader, TableBody, and TableFooter components.
 *
 * @param {React.ReactNode} children - The content of the table.
 * @param {string} className - Additional CSS classes to apply to the table.
 * @param {object} props - Additional props to pass to the table element.
 */
export const Table: FC<ComponentProps> = memo(({ children, className = '', ...props }) => {
    return (
        <div className={mergeClassNames(`custom__table`, className)} {...props}>{children}</div>
    )
})
Table.displayName = 'Table'


/**
 * A component that represents the header of a table.
 *
 * This component is responsible for rendering the header of a table.
 * It expects its children to be TableHeaderCell components.
 *
 * @param {React.ReactNode} children - The content of the header.
 * @param {string} className - Additional CSS classes to apply to the header.
 * @param {object} props - Additional props to pass to the header element.
 */
export const TableHeader: FC<ComponentProps> = memo(({ children, className = '', ...props }) => {
    return (
        <div className={mergeClassNames(`custom__table__heaader`, className)} {...props}>
            {children}
        </div>
    )
})
TableHeader.displayName = 'TableHeader'

/**
 * A component that represents a cell in a table header.
 *
 * This component is responsible for rendering a cell in a table header.
 * It expects its children to be TableBodyCell components.
 *
 * @param {React.ReactNode} children - The content of the cell.
 * @param {'small' | 'medium' | 'large'} cellType - The type of column to render.
 * @param {string} className - Additional CSS classes to apply to the cell.
 * @param {object} props - Additional props to pass to the cell element.
 */
export const TableHeaderCell: FC<ComponentProps & { cellType?: CellTypes }> = memo(({ children, cellType = '', className = '', ...props }) => {
    return (
        <div className={mergeClassNames(`custom__table__header__column ${cellType}`, className)} {...props}>
            {children}
        </div>
    )
})
TableHeaderCell.displayName = 'TableHeaderCell'


/**
 * A component that represents the body of a table.
 *
 * This component is responsible for rendering the content of a table body.
 * It expects its children to be TableBodyRow components.
 *
 * @param {React.ReactNode} children - The content of the table body.
 * @param {string} className - Additional CSS classes to apply to the table body.
 * @param {object} props - Additional props to pass to the table body element.
 */
export const TableBody: FC<ComponentProps> = memo(({ children, className = '', ...props }) => {
    return (
        <div className={mergeClassNames(`custom__table__body`, className)} {...props}>
            {children}
        </div>
    )
})
TableBody.displayName = 'TableBody'


/**
 * A component that represents a row in a table body.
 *
 * This component is responsible for rendering a row in a table body.
 * It expects its children to be TableBodyCell components.
 *
 * @param {string} className - Additional CSS classes to apply to the row.
 * @param {object} props - Additional props to pass to the row element.
 */
export const TableBodyRow: FC<ComponentProps> = memo(({ children, className = '', ...props }) => {
    return (
        <div className={mergeClassNames(`custom__table__body__row`, className)} {...props}>
            {children}
        </div>
    )
})
TableBodyRow.displayName = 'TableBodyRow'


/**
 * A component that represents a cell in a table body.
 *
 * This component is responsible for rendering the contents of a cell in a table body.
 * It expects its children to be TableBodyCell components.
 *
 * @param {React.ReactNode} children - The content of the cell.
 * @param {'small' | 'medium' | 'large'} cellType - The type of column to render.
 * @param {string} className - Additional CSS classes to apply to the cell.
 * @param {object} props - Additional props to pass to the cell element.
 */
export const TableBodyCell: FC<ComponentProps & { cellType?: CellTypes }> = memo(({ children, cellType = '', className = '', ...props }) => {
    return (
        <div className={mergeClassNames(`custom__table__body__column ${cellType}`, className)} {...props}>
            {children}
        </div>
    )
})
TableBodyCell.displayName = 'TableBodyCell'

/**
 * A component that represents a column in a table body.
 *
 * This component is responsible for rendering a column in a table body.
 * It expects its children to be TableBodyCell components.
 *
 * @param {string} className - Additional CSS classes to apply to the column.
 * @param {object} props - Additional props to pass to the column element.
 */
export const ColumnDevider: FC<ComponentProps> = memo(({ className = '', ...props }) => {
    return (
        <div className={mergeClassNames(`custom__table__body__column__devider`, className)} {...props} />
    )
})
ColumnDevider.displayName = 'ColumnDevider'

/**
 * A component that represents a row in a table body.
 *
 * This component is responsible for rendering a row in a table body.
 * It expects its children to be TableBodyCell components.
 *
 * @param {string} className - Additional CSS classes to apply to the row.
 * @param {object} props - Additional props to pass to the row element.
 */
export const RowDevider: FC<ComponentProps> = memo(({ className = '', ...props }) => {
    return (
        <div className={mergeClassNames(`custom__table__body__row__devider`, className)} {...props} />
    )
})
RowDevider.displayName = 'RowDevider'
